import React from 'react';

/**
 * "Who has / hasn't approved this gate", and "have I already acted" --
 * GitHub issue #221.
 *
 * Before this, the queue's Progress column was a bare "N of M approved"
 * string with nothing to click -- an approver could not see WHO the other
 * N-1 approvers were, or (on an ALL gate, which stays in the queue after you
 * act) confirm that their own approval or rejection had actually registered.
 *
 * PURE PRESENTATION, NO FETCH. `approval` is the PendingApprovalResponse row
 * the caller already holds (ApprovalsPage's table row, or
 * ApprovalActionBar's single fetched row) -- approved_member_ids,
 * rejected_member_ids and viewer_member_id are all computed SERVER-SIDE by
 * the same workflow_service.step_progress function approve_step itself
 * calls to decide completion, so this modal can never disagree with what a
 * second approve/reject attempt would actually do.
 *
 * actions_so_far is DELIBERATELY widened to every cycle of this gate (see
 * _build_pending_response's own comment in routers/approvals.py), so a
 * member's status row below is resolved from approved_member_ids /
 * rejected_member_ids -- the CURRENT cycle only -- while a prior cycle's
 * actions are shown separately, muted, and are never counted toward the
 * roster above them.
 */
function ApprovalProgressModal({ approval, onClose }) {
  if (!approval) return null;

  const members = approval.members || [];
  const approvedIds = new Set(approval.approved_member_ids || []);
  const rejectedIds = new Set(approval.rejected_member_ids || []);
  const currentCycleActions = (approval.actions_so_far || []).filter(
    (a) => a.step_instance_id === approval.id,
  );
  const priorCycleActions = (approval.actions_so_far || []).filter(
    (a) => a.step_instance_id !== approval.id,
  );

  const actionFor = (memberId) => currentCycleActions.find((a) => a.member_id === memberId);

  const statusFor = (memberId) => {
    if (approvedIds.has(memberId)) return { icon: '✅', label: 'Approved' };
    if (rejectedIds.has(memberId)) return { icon: '⛔', label: 'Returned' };
    return { icon: '⏳', label: 'Awaiting' };
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Gate {approval.step_order} of {approval.total_steps}: {approval.group_name}</h3>
        </div>

        <p style={{ color: 'var(--muted)', fontSize: '13px', marginTop: 0 }}>
          {approval.completion_rule === 'all'
            ? 'All members must approve.'
            : 'Any one member approving is enough.'}
        </p>

        <div>
          {members.map((member) => {
            const status = statusFor(member.id);
            const action = actionFor(member.id);
            const isViewer = approval.viewer_member_id === member.id;
            return (
              <div
                key={member.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <span style={{ fontSize: '16px', lineHeight: '20px' }}>{status.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13.5px' }}>
                    {member.name}
                    {isViewer && (
                      <span style={{ color: 'var(--muted)', fontWeight: 400 }}> (you)</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                    {status.label}
                    {action?.delegate_name && ` — on behalf of ${action.delegate_name}`}
                    {action?.acted_at && ` · ${new Date(action.acted_at).toLocaleString()}`}
                  </div>
                  {action?.comment && (
                    <div style={{ fontSize: '12.5px', color: 'var(--ink-soft)', marginTop: '2px' }}>
                      &ldquo;{action.comment}&rdquo;
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {priorCycleActions.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'var(--ink-soft)',
              marginBottom: '6px',
            }}>
              Earlier rounds of this gate
            </div>
            {priorCycleActions.map((a) => (
              <div key={a.id ?? `${a.member_id}-${a.acted_at}`} style={{
                fontSize: '12.5px', color: 'var(--muted)', padding: '4px 0',
              }}>
                {a.action === 'approve' ? '✅' : '⛔'} {a.member_name}
                {a.delegate_name && ` (on behalf of, via ${a.delegate_name})`}
                {' · '}{new Date(a.acted_at).toLocaleString()}
              </div>
            ))}
          </div>
        )}

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default ApprovalProgressModal;
